import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    farmId: Number,
    fieldCycleId: Number,
  };

  connect() {
    this.boundHandleSubmitEnd = this.#handleSubmitEnd.bind(this);
    document.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
  }

  openCreate(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_create_modal");
  }

  openEditModal(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_edit_modal");
  }

  openDeleteModal(event) {
    event.stopPropagation();
    const deleteUrl = event.currentTarget.dataset.url;
    if (deleteUrl) {
      const modal = this.element.querySelector("#shared_delete_modal");
      if (modal) {
        const form = modal.querySelector("[data-delete-form]");
        if (form) form.action = deleteUrl;
        modal.showModal();
      }
      return;
    }
    // Legacy fallback
    const modalId = event.currentTarget.dataset.modalId;
    const modal = document.getElementById(modalId);
    if (modal) modal.showModal();
  }

  openDuplicateModal(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_duplicate_modal");
  }

  openLogModal(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_log_modal");
  }

  openRecordExitsModal(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_record_exits_modal", true);
  }

  openLogsSummaryModal(event) {
    event.stopPropagation();
    this.#openSharedOrLegacy(event, "shared_logs_summary_modal", true);
  }

  closeModal(event) {
    const modal = event.target.closest("dialog");
    if (modal) {
      modal.close();
    }
  }

  #openSharedOrLegacy(event, sharedModalId, alwaysFresh = false) {
    const url = event.currentTarget.dataset.url;
    const frameId = event.currentTarget.dataset.frameId;

    if (url && frameId) {
      const modal = this.element.querySelector(`#${sharedModalId}`);
      if (!modal) return;
      const frame = modal.querySelector("turbo-frame");
      if (frame) {
        frame.id = frameId;
        frame.innerHTML =
          '<div class="flex justify-center py-8"><span class="loading loading-spinner loading-lg"></span></div>';
        frame.src = url;
      }
      modal.showModal();
      return;
    }

    // Legacy: per-item modal by ID
    const modalId = event.currentTarget.dataset.modalId;
    const legacyModal = document.getElementById(modalId);
    if (legacyModal) {
      if (alwaysFresh) {
        this.#triggerAlwaysFreshLoad(legacyModal);
      } else {
        this.#triggerLazyLoad(legacyModal);
      }
      legacyModal.showModal();
    }
  }

  #triggerLazyLoad(modal) {
    const lazyModalController = modal.dataset.lazyModalUrlValue;
    if (lazyModalController) {
      const turboFrame = modal.querySelector("turbo-frame");
      if (turboFrame && !turboFrame.src) {
        turboFrame.src = lazyModalController;
      }
    }
  }

  // Always re-fetch content so the summary reflects the latest data
  #triggerAlwaysFreshLoad(modal) {
    const url = modal.dataset.lazyModalUrlValue;
    if (url) {
      const turboFrame = modal.querySelector("turbo-frame");
      if (turboFrame) {
        turboFrame.src = url;
      }
    }
  }

  #closeAllModals() {
    document.querySelectorAll("dialog[open]").forEach((dialog) => {
      dialog.close();
    });
  }

  #handleSubmitEnd(event) {
    const { success, fetchResponse } = event.detail;

    // Only close modal and refresh on successful submissions (2xx status codes)
    // For validation errors (422), keep the modal open to show errors
    if (!success) return;

    // Additional check: ensure it's a 2xx response, not a redirect or other status
    const status = fetchResponse?.response?.status;
    if (status && status >= 400) return;

    const form = event.target;
    const turboVisitUrl =
      fetchResponse?.response?.headers?.get("X-Turbo-Visit");
    const keepModalOpen =
      fetchResponse?.response?.headers?.get("X-Modal-Keep-Open") === "true";

    if (form.hasAttribute("data-modal-no-refresh")) {
      const closeOnSuccess =
        form.hasAttribute("data-close-modal-on-success") && !keepModalOpen;
      if (closeOnSuccess || turboVisitUrl) {
        this.#closeAllModals();
      }
      if (turboVisitUrl) window.Turbo.visit(turboVisitUrl);
      return;
    }

    const dialog = form.closest("dialog");
    if (!dialog) return;

    this.#closeAllModals();

    // Full page visit when the server asks for one (e.g. the cycle's first line items)
    if (turboVisitUrl) {
      window.Turbo.visit(turboVisitUrl);
      return;
    }

    const frame =
      document.querySelector("turbo-frame#field_cycle_content") ||
      document.querySelector("turbo-frame#line_item_content");
    if (frame) {
      // Save scroll position before refresh
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      // Get target URL from response header (includes correct week parameter)
      // This ensures we stay on the week where the task was created/edited
      const targetUrl = this.#preserveCurrentParams(
        fetchResponse?.response?.headers?.get("X-Target-Url") ||
          window.location.href,
      );

      // Update the browser URL to reflect the correct week
      if (targetUrl !== window.location.href) {
        window.history.replaceState({}, "", targetUrl);
      }

      // Reload the frame and restore scroll position after load
      frame.addEventListener(
        "turbo:frame-load",
        () => {
          window.scrollTo(scrollX, scrollY);
        },
        { once: true },
      );

      frame.src = targetUrl;
    }
  }

  // Preserve work-area state (tab, rows, day, lens, period) and filter params
  // from the current URL in the target URL
  #preserveCurrentParams(targetUrl) {
    const current = new URL(window.location.href);
    const target = new URL(targetUrl, window.location.origin);

    for (const key of ["tab", "rows", "day", "lens", "period"]) {
      const value = current.searchParams.get(key);
      if (value && !target.searchParams.has(key)) {
        target.searchParams.set(key, value);
      }
    }

    // Preserve filter params (q[...])
    for (const [key, value] of current.searchParams) {
      if (key.startsWith("q[") && !target.searchParams.has(key)) {
        target.searchParams.set(key, value);
      }
    }

    return target.toString();
  }
}
